var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
import React from 'react';
import styled, { css } from 'styled-components';
import { isArray, includes, uniq, sortBy, template, flatMap } from 'lodash';
import { breakpoints, theme } from '@shapeable/theme';
import { EntitySelect, entitySelectSlugGptTransform, useEntity, useEntityTypeDefinitions, useLang, usePageEntity } from '@shapeable/ui';
import { chatValueIsEntity, classNames, entityTypeNameFor } from '@shapeable/utils';
var cls = classNames('prompt-variable-select');
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    width: 100%;\n    max-width: 500px;\n  "], ["\n    width: 100%;\n    max-width: 500px;\n  "])))
});
var SelectStyles = breakpoints({
  base: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    width: 100%;\n\n    .shp--entity-select__control {\n      border-radius: 10px;\n      border: 1px solid ", ";\n    }\n\n    .shp--entity-select__indicators {\n      margin-top: 20px;\n    }\n\n    .shp--entity-select__placeholder {\n      color: ", ";\n      margin-left: 5px;\n    }\n\n    .shp--entity-select__dropdown-indicator {\n      svg {\n        ", "\n      }\n    }\n\n    .shp--entity-select__option--is-selected {\n      background-color: ", ";\n      color: ", ";\n      .shp--entity-value__body {\n        color: ", ";\n        ", ";\n        .shp--cell-label {\n          color: ", ";\n        }\n      }\n    }\n\n    .shp--entity-base-select__input-container {\n      margin-left: -2px;\n    }\n\n    .shp--entity-select__value-container {\n      padding-left: 0;\n    }\n\n    .shp--slim-select__menu {\n      background-color: #333;\n      color: #FFF;\n    }\n\n    .shp--slim-select__menu-list {\n      padding-bottom: 0;\n    }\n\n    .shp--slim-select__option {\n      border-color: #666;\n\n      &:last-child {\n        border: none;\n      }\n    }\n\n  "], ["\n    width: 100%;\n\n    .shp--entity-select__control {\n      border-radius: 10px;\n      border: 1px solid ", ";\n    }\n\n    .shp--entity-select__indicators {\n      margin-top: 20px;\n    }\n\n    .shp--entity-select__placeholder {\n      color: ", ";\n      margin-left: 5px;\n    }\n\n    .shp--entity-select__dropdown-indicator {\n      svg {\n        ", "\n      }\n    }\n\n    .shp--entity-select__option--is-selected {\n      background-color: ", ";\n      color: ", ";\n      .shp--entity-value__body {\n        color: ", ";\n        ", ";\n        .shp--cell-label {\n          color: ", ";\n        }\n      }\n    }\n\n    .shp--entity-base-select__input-container {\n      margin-left: -2px;\n    }\n\n    .shp--entity-select__value-container {\n      padding-left: 0;\n    }\n\n    .shp--slim-select__menu {\n      background-color: #333;\n      color: #FFF;\n    }\n\n    .shp--slim-select__menu-list {\n      padding-bottom: 0;\n    }\n\n    .shp--slim-select__option {\n      border-color: #666;\n\n      &:last-child {\n        border: none;\n      }\n    }\n\n  "])), theme.COLOR('line-subtle'), theme.COLOR('text-subtle'), theme.FILL('text-subtle'), theme.COLOR('strong-chrome'), theme.COLOR('bright'), theme.COLOR('bright'), theme.FILL('bright'), theme.COLOR('bright'))
});
// -------- Components -------->
var My = {
  Container: styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Select: styled(EntitySelect)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["", ""], ["", ""])), SelectStyles)
};
export var promptVariableSelectOnChange = function (onChange) {
  return function (value) {
    if (isArray(value)) {
      onChange(value.map(function (item) {
        return item.entity;
      }));
    } else {
      if (value) {
        onChange(value.entity);
      } else {
        onChange(null);
      }
    }
  };
};
export var PromptVariableEntitySelect = function (props) {
  var _a;
  var className = props.className,
    children = props.children,
    value = props.value,
    disabled = props.disabled,
    title = props.title,
    contextEntity = props.contextEntity,
    autoFocus = props.autoFocus;
  var entity = useEntity(props.entity);
  var pageEntity = usePageEntity();
  var entityTypeName = entityTypeNameFor(pageEntity);
  var defns = useEntityTypeDefinitions();
  var t = useLang();
  // resolve the entity type definitions
  var entityTypeNames = [];
  var controlTypeField = contextEntity ? 'controlTypeContextual' : 'controlType';
  var entityTypesField = contextEntity ? 'entityTypesContextual' : 'entityTypes';
  var fallbackControlTypeField = contextEntity ? 'controlType' : 'controlTypeContextual';
  var fallbackEntityTypesField = contextEntity ? 'entityTypes' : 'entityTypesContextual';
  var entityDict = entity;
  var controlType = ((_a = entityDict[controlTypeField] || entityDict[fallbackControlTypeField]) === null || _a === void 0 ? void 0 : _a.slug) || 'entity-select-current-entity-selected';
  if (includes(['entity-select-current-entity-selected', 'entity-select-current-entity-type'], controlType)) {
    // push the current entity
    entityTypeNames.push(entityTypeName);
  }
  // now add any specific entities setup in the platform
  var specificEntityTypeNames = (entityDict[entityTypesField] || entityDict[fallbackEntityTypesField] || []).map(function (entityType) {
    return entityType.internalName || entityType.name;
  });
  var allEntityTypeNames = uniq(entityTypeNames.concat(specificEntityTypeNames));
  var entityTypeLabels = allEntityTypeNames.map(function (typeName) {
    var _a;
    return ((_a = defns[typeName]) === null || _a === void 0 ? void 0 : _a.label) || typeName;
  });
  var data = sortBy(flatMap(allEntityTypeNames, function (typeName) {
    var _a;
    return ((_a = defns[typeName]) === null || _a === void 0 ? void 0 : _a.data()) || [];
  }), 'name');
  var placeholder = template(t('Select <%= label %>'))({
    label: entityTypeLabels.join(" or ")
  });
  // console.log(allEntityTypeNames, data);
  return React.createElement(My.Container, {
    title: title,
    className: cls.name(className)
  }, React.createElement(My.Select, {
    autoFocus: autoFocus,
    isDisabled: disabled,
    value: value && chatValueIsEntity(value) && entitySelectSlugGptTransform(value),
    placeholder: placeholder,
    transform: entitySelectSlugGptTransform,
    onChange: promptVariableSelectOnChange(props.onChange),
    showSearchIcon: false,
    items: data
  }));
};
PromptVariableEntitySelect.cls = cls;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;